import React, { useState, useRef, useEffect } from "react";
import { PrimaryButton, SecondaryButton } from "../../../globalComponents/ui/Button";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";

const API = config.POSTLOGIN_API_URL_COMPANY;

const blankForm = () => ({
  module: "", category: "", subCategory: "", kpiId: "", groupByField: "",
  enableKpiLevel: false,
  kpiLevel: { category: "", subCategories: [], kpiIds: [] },
});

const GROUP_BY_RANK = { category: 1, subCategory: 2, kpi: 3 };
const kpiLvlDisabled = (field, gbf) => GROUP_BY_RANK[field] <= GROUP_BY_RANK[gbf];

const topCatsOf = (cascade, module) => {
  if (!module || !cascade[module]) return "All";
  const cats = Object.keys(cascade[module]);
  return cats.length > 0 ? cats.join(", ") : "All";
};

const getAllSubCats = (cascade, module, category) => {
  if (!module) return [];
  if (category) return Object.keys(cascade[module]?.[category] || {});
  return Object.values(cascade[module] || {}).flatMap((cats) => Object.keys(cats));
};

const getAllKpis = (cascade, module, category, subCats) => {
  if (!module || !category) return [];
  const effectiveSubs = subCats?.length > 0 ? subCats : Object.keys(cascade[module]?.[category] || {});
  return effectiveSubs.flatMap((sc) => cascade[module]?.[category]?.[sc] || []);
};

/* ── MultiSelectDropdown ── */
const MultiSelectDropdown = ({ options, selected, onChange, disabled, placeholder = "All" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);

  const label =
    selected.length === 0 ? placeholder :
    selected.length === options.length ? "All" :
    selected.join(", ");

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        className="kpi-input"
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          userSelect: "none", overflow: "hidden" }}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{label}</span>
        <span style={{ flexShrink: 0, marginLeft: 4, opacity: 0.6, fontSize: 10 }}>▾</span>
      </div>
      {open && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
          background: "var(--surface)", border: "1.5px solid var(--rule)", borderRadius: 8,
          zIndex: 200, maxHeight: 180, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        }}>
          {options.length === 0 ? (
            <div style={{ padding: "8px 10px", color: "var(--ink-4)", fontSize: 12 }}>No options</div>
          ) : (
            <>
              <label style={{ display: "flex", alignItems: "center", padding: "6px 10px", fontSize: 12,
                borderBottom: "1px solid var(--rule-2)", cursor: "pointer", color: "var(--ink-3)" }}>
                <input type="checkbox" className="kpi-checkbox" style={{ marginRight: 8 }}
                  checked={selected.length === options.length}
                  onChange={() => onChange(selected.length === options.length ? [] : [...options])} />
                Select all
              </label>
              {options.map((opt) => (
                <label key={opt} style={{ display: "flex", alignItems: "center", padding: "6px 10px", cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" className="kpi-checkbox" style={{ marginRight: 8 }}
                    checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                  {opt}
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── KPI Level form section ── */
const KpiLevelSection = ({
  form, setForm, gbf, kpiLevelReady,
  kpiLvlCategory, kpiLvlCats, kpiLvlSubCats, kpiLvlKpiList,
  onKpiLvlCategory, onKpiLvlSubCategories, onKpiLvlKpiIds,
}) => {
  const kd = (field) => kpiLvlDisabled(field, gbf);
  const dim = (d) => ({ opacity: d ? 0.5 : 1 });

  return (
    <div style={{ borderTop: "1.5px solid var(--rule)", paddingTop: 14, marginBottom: 14 }}>
      {!kpiLevelReady ? (
        <div className="kpi-label" style={{ color: "var(--ink-4)" }}>
          Fill the Top Level fields above up to the selected Group By level to configure KPI Level
        </div>
      ) : (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 12 }}>
            <input type="checkbox" className="kpi-checkbox"
              checked={form.enableKpiLevel}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm((p) => ({ ...p, enableKpiLevel: checked, kpiLevel: blankForm().kpiLevel }));
              }}
            />
            <div>
              <span style={{ fontWeight: 700, fontSize: 12, color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                KPI Level
              </span>
              <span style={{ fontSize: 12, color: "var(--ink-4)", marginLeft: 8 }}>
                Optional drill-down rows — fields below Group By are multi-select; others are inherited
              </span>
            </div>
          </label>

          {form.enableKpiLevel && (
            <div style={{ marginLeft: 26, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <div style={dim(true)}>
                <label className="kpi-label">Module</label>
                <select className="kpi-input" value={form.module} disabled>
                  <option value={form.module}>{form.module || "—"}</option>
                </select>
              </div>
              <div style={dim(kd("category"))}>
                <label className="kpi-label">Category</label>
                <select className="kpi-input" value={kpiLvlCategory}
                  disabled={kd("category") || !form.module}
                  onChange={(e) => onKpiLvlCategory(e.target.value)}>
                  <option value="">All categories</option>
                  {kpiLvlCats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={dim(kd("subCategory"))}>
                <label className="kpi-label">Sub Category</label>
                {kd("subCategory") ? (
                  <select className="kpi-input" value={form.subCategory || ""} disabled>
                    <option value={form.subCategory || ""}>{form.subCategory || "All sub-categories"}</option>
                  </select>
                ) : (
                  <MultiSelectDropdown
                    options={kpiLvlSubCats}
                    selected={form.kpiLevel.subCategories}
                    onChange={onKpiLvlSubCategories}
                    disabled={!kpiLvlCategory}
                    placeholder="All sub-categories"
                  />
                )}
              </div>
              <div style={dim(kd("kpi"))}>
                <label className="kpi-label">KPI</label>
                <MultiSelectDropdown
                  options={kpiLvlKpiList}
                  selected={form.kpiLevel.kpiIds}
                  onChange={onKpiLvlKpiIds}
                  disabled={kd("kpi") || !form.module}
                  placeholder="All KPIs"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── Config table row ── */
const ConfigRow = ({ c, i, cascade, onEdit, onDelete }) => {
  const kl = c.kpiLevel;
  const useKpiLevel = kl && (kl.subCategories?.length > 0 || kl.kpiIds?.length > 0);
  const subCatSource = useKpiLevel ? kl.subCategories : c.subCategories;
  const kpiSource    = useKpiLevel ? kl.kpiIds        : c.kpiIds;

  const subCatDisplay = subCatSource?.length > 0
    ? subCatSource.join(", ")
    : getAllSubCats(cascade, c.module, c.category).join(", ") || "—";
  const kpiDisplay = kpiSource?.length > 0
    ? kpiSource.join(", ")
    : getAllKpis(cascade, c.module, c.category, subCatSource).join(", ") || "All";

  const gbfValue =
    c.groupByField === "category"    ? (c.category || topCatsOf(cascade, c.module)) :
    c.groupByField === "subCategory" ? subCatDisplay :
    kpiDisplay;

  const td = { padding: "9px 12px" };

  return (
    <tr style={{ background: i % 2 === 0 ? "var(--surface)" : "var(--surface-2)", borderBottom: "1px solid var(--rule-2)" }}>
      <td style={td}><span style={{ fontWeight: 600, color: "#2563eb", fontSize: 12 }}>{c.module}</span></td>
      <td style={{ ...td, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {c.category || <span style={{ fontSize: 12 }}>{topCatsOf(cascade, c.module)}</span>}
      </td>
      <td style={{ ...td, overflow: "hidden" }}>
        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{c.groupByField}</span>
        <span style={{ color: "var(--ink-4)", fontSize: 12, marginLeft: 6 }}>→ {gbfValue}</span>
      </td>
      <td style={{ ...td, color: "var(--ink-3)", fontSize: 12, whiteSpace: "normal", wordBreak: "break-word" }}>
        {kpiDisplay}
      </td>
      <td style={td}>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="kpi-action-btn"
            style={{ background: "#eff6ff", color: "#2563eb", border: "1.5px solid #bfdbfe" }}
            onClick={() => onEdit(c)}>Edit</button>
          <button className="kpi-action-btn"
            style={{ background: "#fef2f2", color: "#dc2626", border: "1.5px solid #fecaca" }}
            onClick={() => onDelete(c.id)}>Delete</button>
        </div>
      </td>
    </tr>
  );
};

/* ══ MAIN COMPONENT ══ */
const IntensityNumeratorConfig = ({ configs, cascade, onRefresh }) => {
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState(blankForm());
  const [saving,    setSaving]    = useState(false);
  const [editingId, setEditingId] = useState(null);

  const gbf = form.groupByField;

  const groupByOptions = [
    ...(form.category    ? [{ value: "category",    label: "Category"     }] : []),
    ...(form.subCategory ? [{ value: "subCategory", label: "Sub Category" }] : []),
    ...(form.kpiId       ? [{ value: "kpi",         label: "KPI"          }] : []),
  ];

  /* ── Top Level handlers ── */
  const onTopModule = (val) => setForm({ ...blankForm(), module: val });
  const onTopCategory = (val) => setForm((p) => ({
    ...p, category: val, subCategory: "", kpiId: "",
    kpiLevel: blankForm().kpiLevel,
    groupByField: val ? p.groupByField : "",
  }));
  const onTopSubCategory = (val) => setForm((p) => ({
    ...p, subCategory: val, kpiId: "",
    groupByField: val ? p.groupByField
      : (p.groupByField === "subCategory" || p.groupByField === "kpi" ? "" : p.groupByField),
  }));
  const onTopKpi = (val) => setForm((p) => ({
    ...p, kpiId: val,
    groupByField: val ? p.groupByField : (p.groupByField === "kpi" ? "" : p.groupByField),
  }));

  /* ── KPI Level handlers ── */
  const onKpiLvlCategory = (val) => {
    const allSubCats = val ? Object.keys(cascade[form.module]?.[val] || {}) : [];
    const allKpis    = allSubCats.flatMap((sc) => cascade[form.module]?.[val]?.[sc] || []);
    setForm((p) => ({ ...p, kpiLevel: { ...p.kpiLevel, category: val, subCategories: allSubCats, kpiIds: allKpis } }));
  };
  const onKpiLvlSubCategories = (vals) => {
    const cat     = form.kpiLevel.category || form.category;
    const allKpis = vals.flatMap((sc) => cascade[form.module]?.[cat]?.[sc] || []);
    setForm((p) => ({ ...p, kpiLevel: { ...p.kpiLevel, subCategories: vals, kpiIds: allKpis } }));
  };
  const onKpiLvlKpiIds = (vals) => setForm((p) => ({ ...p, kpiLevel: { ...p.kpiLevel, kpiIds: vals } }));

  const handleGroupByChange = (val) => setForm((p) => ({
    ...p,
    groupByField:   val,
    enableKpiLevel: val === "kpi" ? false : p.enableKpiLevel,
    kpiLevel:       val === "kpi" ? blankForm().kpiLevel : p.kpiLevel,
  }));

  /* ── Derived values ── */
  const resolvedCatForSub = form.category
    || (form.module && form.subCategory
      ? Object.keys(cascade[form.module] || {}).find((c) => cascade[form.module][c][form.subCategory])
      : null);

  const kpiLvlCategory = kpiLvlDisabled("subCategory", gbf)
    ? resolvedCatForSub || form.category
    : form.category;

  const kpiLevelReady =
    gbf === "category"    ? !!(form.module && form.category) :
    gbf === "subCategory" ? !!(form.module && form.subCategory) :
    false;

  const topModules = Object.keys(cascade);
  const topCats    = form.module ? Object.keys(cascade[form.module] || {}) : [];
  const topSubCats = form.module
    ? form.category
      ? Object.keys(cascade[form.module]?.[form.category] || {})
      : Object.values(cascade[form.module] || {}).flatMap((catObj) => Object.keys(catObj))
    : [];
  const topKpiList = form.module
    ? form.subCategory
      ? (cascade[form.module]?.[resolvedCatForSub]?.[form.subCategory] || [])
      : resolvedCatForSub
        ? Object.values(cascade[form.module]?.[resolvedCatForSub] || {}).flat()
        : Object.values(cascade[form.module] || {}).flatMap((catObj) => Object.values(catObj).flat())
    : [];

  const kpiLvlCats    = form.module ? Object.keys(cascade[form.module] || {}) : [];
  const kpiLvlSubCats = form.module && kpiLvlCategory ? Object.keys(cascade[form.module]?.[kpiLvlCategory] || {}) : [];
  const effectiveKpiSubCats = kpiLvlDisabled("subCategory", gbf)
    ? (form.subCategory ? [form.subCategory] : [])
    : form.kpiLevel.subCategories;
  const kpiLvlKpiList = form.module && kpiLvlCategory
    ? effectiveKpiSubCats.length > 0
      ? effectiveKpiSubCats.flatMap((sc) => cascade[form.module]?.[kpiLvlCategory]?.[sc] || [])
      : Object.values(cascade[form.module]?.[kpiLvlCategory] || {}).flat()
    : [];

  /* ── Save / Edit / Delete ── */
  const handleSave = async () => {
    if (!form.module || !form.groupByField) {
      alert("Module and Group By field are required.");
      return;
    }
    const incomingKpis = [...(form.enableKpiLevel ? form.kpiLevel.kpiIds : (form.kpiId ? [form.kpiId] : []))].sort().join(',');
    const isDuplicate = (configs || []).some((c) => {
      if (c.id === editingId) return false;
      if (c.module !== form.module || (c.category || '') !== (form.category || '') || c.groupByField !== form.groupByField) return false;
      const existingKpis = [...(c.kpiLevel?.kpiIds?.length ? c.kpiLevel.kpiIds : c.kpiIds || [])].sort().join(',');
      return existingKpis === incomingKpis;
    });
    if (isDuplicate) { alert("An identical config already exists."); return; }

    setSaving(true);
    const payload = {
      module:        form.module,
      category:      form.category || undefined,
      subCategories: form.subCategory ? [form.subCategory] : [],
      kpiIds:        form.kpiId      ? [form.kpiId]        : [],
      groupByField:  form.groupByField,
      ...(form.enableKpiLevel && gbf !== "kpi" && {
        kpiLevel: { subCategories: form.kpiLevel.subCategories, kpiIds: form.kpiLevel.kpiIds },
      }),
    };
    const res = editingId
      ? await apiCall(`${API}intensity/config/${editingId}`, {}, payload, "PATCH")
      : await apiCall(`${API}intensity/config`, {}, payload, "POST");
    setSaving(false);
    if (res?.isSuccess) { setForm(blankForm()); setShowForm(false); setEditingId(null); onRefresh(); }
  };

  const handleEdit = (c) => {
    setForm({
      module:         c.module || "",
      category:       c.category || "",
      subCategory:    c.subCategories?.[0] || "",
      kpiId:          c.kpiIds?.[0] || "",
      groupByField:   c.groupByField || "",
      enableKpiLevel: !!(c.kpiLevel && (c.kpiLevel.subCategories?.length > 0 || c.kpiLevel.kpiIds?.length > 0)),
      kpiLevel: {
        category:      c.kpiLevel?.category || "",
        subCategories: c.kpiLevel?.subCategories || [],
        kpiIds:        c.kpiLevel?.kpiIds || [],
      },
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    await apiCall(`${API}intensity/config/${id}`, {}, {}, "DELETE");
    onRefresh();
  };

  const closeForm = () => { setForm(blankForm()); setShowForm(false); setEditingId(null); };

  /* ── Render ── */
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>Numerator Configuration</div>
          <div className="kpi-label" style={{ marginTop: 4 }}>
            Each config defines what each accordion row represents (Top Level) and an optional drill-down (KPI Level)
          </div>
        </div>
        {!showForm && <PrimaryButton onClick={() => setShowForm(true)}>+ Add Config</PrimaryButton>}
      </div>

      {showForm && (
        <div style={{ background: "var(--surface-2)", border: "1.5px dashed var(--rule)", borderRadius: 10, padding: 18, marginBottom: 18 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Top Level
            </span>
            <span style={{ fontSize: 12, color: "var(--ink-4)", marginLeft: 8 }}>
              Select fields to filter data — Group By options appear based on your selection
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
            <div>
              <label className="kpi-label">Module <span style={{ color: "#dc2626" }}>*</span></label>
              <select className="kpi-input" value={form.module} onChange={(e) => onTopModule(e.target.value)}>
                <option value="">Select module</option>
                {topModules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="kpi-label">Category</label>
              <select className="kpi-input" value={form.category} disabled={!form.module}
                onChange={(e) => onTopCategory(e.target.value)}>
                <option value="">All categories</option>
                {topCats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ opacity: !form.module ? 0.5 : 1 }}>
              <label className="kpi-label">Sub Category</label>
              <select className="kpi-input" value={form.subCategory} disabled={!form.module}
                onChange={(e) => onTopSubCategory(e.target.value)}>
                <option value="">All sub-categories</option>
                {topSubCats.map((sc) => <option key={sc} value={sc}>{sc}</option>)}
              </select>
            </div>
            <div>
              <label className="kpi-label">KPI</label>
              <select className="kpi-input" value={form.kpiId} disabled={!form.module}
                onChange={(e) => onTopKpi(e.target.value)}>
                <option value="">All KPIs</option>
                {topKpiList.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="kpi-label">Group By <span style={{ color: "#dc2626" }}>*</span></label>
            <select
              className="kpi-input"
              style={{ maxWidth: 260, opacity: groupByOptions.length === 0 ? 0.5 : 1 }}
              value={form.groupByField}
              disabled={groupByOptions.length === 0}
              onChange={(e) => handleGroupByChange(e.target.value)}
            >
              <option value="">
                {groupByOptions.length === 0 ? "Select category / sub category / KPI first" : "Select group by field"}
              </option>
              {groupByOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {gbf && gbf !== "kpi" && (
            <KpiLevelSection
              form={form} setForm={setForm} gbf={gbf}
              kpiLevelReady={kpiLevelReady}
              kpiLvlCategory={kpiLvlCategory}
              kpiLvlCats={kpiLvlCats}
              kpiLvlSubCats={kpiLvlSubCats}
              kpiLvlKpiList={kpiLvlKpiList}
              onKpiLvlCategory={onKpiLvlCategory}
              onKpiLvlSubCategories={onKpiLvlSubCategories}
              onKpiLvlKpiIds={onKpiLvlKpiIds}
            />
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? "Saving…" : editingId ? "Update" : "Save"}</PrimaryButton>
            <SecondaryButton onClick={closeForm}>Cancel</SecondaryButton>
          </div>
        </div>
      )}

      {configs.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--ink-4)", fontSize: 13, padding: "24px 0" }}>
          No configurations yet. Click "+ Add Config" to get started.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "12%" }} /><col style={{ width: "15%" }} />
            <col style={{ width: "26%" }} /><col style={{ width: "33%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr style={{ background: "var(--surface-2)" }}>
              {["Module", "Category", "Group By", "KPIs", ""].map((h) => (
                <th key={h} style={{
                  padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--ink-3)",
                  borderBottom: "1.5px solid var(--rule)", textTransform: "uppercase",
                  fontSize: 11, letterSpacing: "0.05em",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map((c, i) => (
              <ConfigRow key={c.id} c={c} i={i} cascade={cascade} onEdit={handleEdit} onDelete={handleDelete} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default IntensityNumeratorConfig;
